/**
 * OnboardingModal — 登録した人が**最初に必ず見る画面**。
 *
 * 【設計の前提】
 * - Pop/Light v2 のトークンだけを使う（クリーム地・紫primary・2pxインク枠・ハードシャドウ）。
 *   ここで独自の色を作らない。旧ダークテーマ（bg-gray-900 / neon-*）からの作り直し。
 * - **三役対等**（調停原則1）: 「書き手として」等、特定の役割を前提にした言い回しをしない。
 *   役割色（琥珀/紫/ティール）も使わない ─ 役割色は役割の識別専用で、ここは誰のものでもない画面。
 * - **閉じられない**（名前が無いと他の画面が成立しない）。ただし
 *   「閉じられないこと」に苛立たせないよう、急かさず、理由が分かる文にする。
 * - 文言はすべて日本語。ボタンは変換表準拠（YES/NO 禁止・「！」は主CTAに1つまで）。
 * - 「あとから変えられます」は**事実**（マイページ → プロフィール編集で変更できる）。
 */
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';

interface OnboardingModalProps {
    isOpen: boolean;
    initialName: string;
    onComplete: (user: any) => void;
}

const MAX_NAME = 20;

export function OnboardingModal({ isOpen, initialName, onComplete }: OnboardingModalProps) {
    const [name, setName] = useState(initialName || '');
    const [isLoading, setIsLoading] = useState(false);
    /** 失敗の理由。トーストは消えるので、やり直せるよう画面にも残す */
    const [failure, setFailure] = useState<string | null>(null);
    const { toast } = useToast();
    const { playSound, triggerCelebration } = useGamenics();
    const inputRef = useRef<HTMLInputElement>(null);

    // 開いたら入力欄へ（キーボードだけでも最短で進める）
    useEffect(() => {
        if (isOpen) window.setTimeout(() => inputRef.current?.focus(), 50);
    }, [isOpen]);

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed || isLoading) return;
        playSound(GamenicsEvent.TAP_CONFIRM);
        setFailure(null);
        setIsLoading(true);
        try {
            const result = await api.updateProfile({ displayName: trimmed, isOnboarded: true });
            if (result.success) {
                await triggerCelebration(GamenicsEvent.SUBMISSION_COMPLETE);
                toast({ title: 'ようこそ！', description: `${trimmed} さんとしてはじめます。` });
                onComplete({ displayName: trimmed, isOnboarded: true });
            } else {
                setFailure(result.error || '名前を決められませんでした。もう一度ためしてください。');
            }
        } catch {
            setFailure('通信できませんでした。つながりを確かめて、もう一度ためしてください。');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={() => { }}>
            {/*
              閉じるボタン（×）は Dialog の既定で入るが、この画面は閉じられない。
              押しても何も起きない × を見せるのは苛立たせるだけなので隠す。
              `[&>button]:hidden` は DialogContent の**直下の button**＝×だけに当たる
              （主CTAは DialogFooter 相当の div の中にあるため影響しない）。
            */}
            <DialogContent
                className="bg-cream border-2 border-ink rounded-pop-md shadow-pop text-ink sm:max-w-[440px] [&>button]:hidden"
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <div className="text-center">
                    <p className="font-display font-bold tracking-[0.18em] text-primary text-xs mb-2">WELCOME</p>
                    <h2 className="font-display font-black text-2xl text-ink">ようこそ、ANOKOEへ</h2>
                    <p className="text-ink-2 text-sm mt-3 leading-relaxed">
                        はじめに、呼ばれたい名前を決めましょう。
                        <br />
                        ここで決めた名前が、<strong className="text-ink">ほかの人に見えます</strong>。
                    </p>
                </div>

                <div className="mt-5">
                    <Label htmlFor="onboarding-name" className="block text-xs font-bold text-ink-2 mb-1">
                        表示される名前
                    </Label>
                    <Input
                        id="onboarding-name"
                        ref={inputRef}
                        value={name}
                        onChange={(e) => { setName(e.target.value); if (failure) setFailure(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleSave(); } }}
                        className="w-full min-h-[44px] bg-surface border-2 border-ink/20 rounded-pop-sm text-ink placeholder:text-ink-3 focus:border-primary focus-visible:ring-0"
                        placeholder="例: あおい"
                        maxLength={MAX_NAME}
                        aria-describedby="onboarding-hint"
                        autoComplete="nickname"
                    />
                    <p id="onboarding-hint" className="text-xs text-ink-2 mt-2">
                        {MAX_NAME}文字まで。あとからマイページで変えられます。
                    </p>
                </div>

                {/* 失敗はトーストだけにせず画面に残す（やり直せるように） */}
                {failure && (
                    <p role="alert" aria-live="assertive" className="text-sm text-pop-dark bg-surface border-2 border-pop-dark rounded-pop-sm px-3 py-2 mt-3">
                        {failure}
                    </p>
                )}

                {/* ⚠️ 主CTAは必ず div で包む。DialogContent 直下に置くと、上の
                    `[&>button]:hidden`（×を隠すための指定）に巻き込まれて消える */}
                <div className="mt-5">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={isLoading || !name.trim()}
                        className="btn btn-primary w-full min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? '準備しています…' : COPY.start}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
